import React, { FC, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import classNames from 'classnames';

import { VALUE_ESCAPE } from 'app/constants/keyValues';
import useOutsideClick from 'app/lib/hooks/useOutsideClickHook';
import { isSSR } from 'app/packages/components';

import { ModalContext } from './context';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

import styles from './Modal.module.scss';


export type ModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  closable?: boolean;
  isClosedByEsc?: boolean;
  isOutsideClickable?: boolean;
  autofocus?: boolean;
  className?: string;
  maxWidth?: number;
  'data-test'?: string;
}>;

type ModalComponent = FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

export const Modal: ModalComponent = ({
  isOpen,
  className,
  closable = true,
  isClosedByEsc = true,
  isOutsideClickable = true,
  autofocus = false,
  maxWidth = 544,
  onClose,
  children,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useOutsideClick(modalRef, () => {
    if (!closable || !isOpen || !isOutsideClickable) {
      return;
    }

    onClose();
  });

  const handleModalCloseByEscape = useCallback(
    (event: globalThis.KeyboardEvent): void => {
      if (isClosedByEsc && event.key === VALUE_ESCAPE && onClose) {
        onClose();
      }
    }, [isClosedByEsc, onClose]);

  useEffect(
    () => {
      document.addEventListener('keyup', handleModalCloseByEscape);

      if (modalRef.current) {
        modalRef.current.querySelector('input')?.focus();
      }

      return (): void => {
        document.removeEventListener('keyup', handleModalCloseByEscape);
      };
    }, [isOpen]);

  if (!isOpen || isSSR) {
    return null;
  }

  return (
    ReactDOM.createPortal(
      (
        <FocusLock autoFocus={false} >
          <div role="dialog"
               className={classNames(styles.modalContainer, className)}
               data-test={props['data-test']}
               aria-modal="true">
            <div className={styles.modalContent}
                 style={{ maxWidth }}
                 ref={modalRef}>
              <ModalContext.Provider value={{
                onClose,
                isOpen,
                closable,
                autofocus,
                isOutsideClickable,
              }}>
                {children}
              </ModalContext.Provider>
            </div>
          </div>
        </FocusLock>
      ),
      document.body,
    )
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
